import * as z from "zod";
import { nanoid } from "nanoid";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";

export const updateUsageBudgetSchema = z.object({
  id: z.string().optional(),
  spaceId: z.string().optional(),
  connectorId: z.string().optional(),
  measure: z.enum(["requests", "tokens", "spend_usd"]),
  limitAmount: z.number().positive(),
  warnThresholdPct: z.number().min(1).max(100).optional(),
});

export const updateUsageBudget = mutator({
  schema: updateUsageBudgetSchema,
  resolve: {},
  permissions: [
    {
      permission: "tenant:ai_providers_manage",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const now = Date.now();
    const spaceId = args.spaceId ?? null;
    const connectorId = args.connectorId ?? null;

    // Resolve by the exact dimension slice, never a caller-supplied id which
    // could name a budget in another scope; the (dimensions, measure) tuple is
    // the natural key enforced by the NULLS NOT DISTINCT unique index. The
    // tenantId filter keeps the unscoped lookup (no RLS) from updating another
    // tenant's budget row for a foreign spaceId/connectorId.
    let scoped = zql.connectorUsageBudgets
      .where("tenantId", ctx.tenantId)
      .where("measure", args.measure);
    scoped =
      spaceId === null
        ? scoped.where("spaceId", "IS", null)
        : scoped.where("spaceId", spaceId);
    scoped =
      connectorId === null
        ? scoped.where("connectorId", "IS", null)
        : scoped.where("connectorId", connectorId);
    const existing = await tx.run(scoped.one());

    const values = {
      spaceId,
      connectorId,
      measure: args.measure,
      limitAmount: args.limitAmount,
      warnThresholdPct:
        args.warnThresholdPct ?? existing?.warnThresholdPct ?? 80,
      updatedAt: now,
    };
    if (existing) {
      await tx.mutate.connectorUsageBudgets.update({
        id: existing.id,
        ...values,
      });
      return;
    }
    await tx.mutate.connectorUsageBudgets.insert({
      id: args.id ?? nanoid(12),
      ...values,
      createdAt: now,
      tenantId: ctx.tenantId,
    });
  },
});
